import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Bus, 
  Clock, 
  Calendar, 
  ChevronRight, 
  Search, 
  ArrowRight, 
  HelpCircle, 
  ChevronDown, 
  MapPin, 
  Share2, 
  Check, 
  Building, 
  Info,
  ShieldCheck,
  PhoneCall
} from 'lucide-react';
import SEO from '../components/SEO';
import BusDisclaimer from '../components/BusDisclaimer';
import routesData from '../data/processedBusRoutes.json';
import { ENQUIRY_NUMBER, DATA_LAST_VERIFIED } from '../config/busConfig';
import { SITE_URL } from '../config/seo';

const OPERATOR_BADGES = {
  HR: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800',
  PRTC: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-950/60 dark:text-purple-300 dark:border-purple-800',
  PVT: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800',
  RJ: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800',
  UK: 'bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-950/60 dark:text-teal-300 dark:border-teal-800',
  PB: 'bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-950/60 dark:text-indigo-300 dark:border-indigo-800'
};

const OPERATOR_NAMES = {
  HR: 'Haryana Roadways',
  PRTC: 'PRTC (Punjab)',
  PVT: 'Private Cooperative',
  RJ: 'Rajasthan Roadways',
  UK: 'Uttarakhand Parivahan',
  PB: 'Punjab Roadways'
};

export default function BusTimeTableDetail() {
  const { routeSlug } = useParams();
  const [selectedVia, setSelectedVia] = useState('ALL');
  const [timeFilter, setTimeFilter] = useState('ALL'); // ALL, MORNING, AFTERNOON, EVENING
  const [searchQuery, setSearchQuery] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [copied, setCopied] = useState(false);

  // Find matching route
  const route = useMemo(() => {
    return routesData.routes.find((r) => r.slug === routeSlug);
  }, [routeSlug]);

  // Lookup related route objects
  const relatedRoutes = useMemo(() => {
    if (!route || !route.related_slugs) return [];
    return route.related_slugs
      .map((slug) => routesData.routes.find((r) => r.slug === slug))
      .filter(Boolean);
  }, [route]);

  // Extract all buses from via_groups
  const allBuses = useMemo(() => {
    if (!route || !route.via_groups) return [];
    const list = [];
    route.via_groups.forEach((g) => {
      g.buses.forEach((b) => list.push(b));
    });
    // Sort strictly by departure_time
    return list.sort((a, b) => a.departure_time.localeCompare(b.departure_time));
  }, [route]);

  // Filtered bus list
  const filteredBuses = useMemo(() => {
    return allBuses.filter((bus) => {
      // Via filter
      if (selectedVia !== 'ALL' && bus.via !== selectedVia) {
        return false;
      }
      // Time filter
      if (timeFilter === 'MORNING') {
        const hour = parseInt(bus.departure_time.split(':')[0], 10);
        if (hour >= 12) return false;
      } else if (timeFilter === 'AFTERNOON') {
        const hour = parseInt(bus.departure_time.split(':')[0], 10);
        if (hour < 12 || hour >= 17) return false;
      } else if (timeFilter === 'EVENING') {
        const hour = parseInt(bus.departure_time.split(':')[0], 10);
        if (hour < 17) return false;
      }
      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTime = bus.departure_time.toLowerCase().includes(q);
        const matchVia = bus.via.toLowerCase().includes(q);
        const matchOp = bus.operator.toLowerCase().includes(q);
        const matchType = bus.service_type.toLowerCase().includes(q);
        if (!matchTime && !matchVia && !matchOp && !matchType) return false;
      }
      return true;
    });
  }, [allBuses, selectedVia, timeFilter, searchQuery]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: route ? route.title : 'Bus Time Table',
        url: window.location.href
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };



  if (!route) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-16 px-4">
        <div className="max-w-2xl mx-auto text-center bg-white dark:bg-gray-900 rounded-2xl p-8 border border-gray-200 dark:border-gray-800 shadow-sm">
          <Bus className="h-16 w-16 text-blue-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Route Time Table Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
            यह बस रूट समय सारिणी उपलब्ध नहीं है या यूआरएल बदल गया है। कृपया मुख्य समय सारिणी हब पर जाएं।
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/bus-time-table"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm transition"
            >
              Fatehabad Bus Time Table Hub
            </Link>
            <Link
              to="/bus-time-table/tohana"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-medium text-sm transition"
            >
              Tohana Bus Time Table
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // JSON-LD structured data: BreadcrumbList, FAQPage, WebPage
  const pageUrl = `${SITE_URL}/bus-time-table/${route.slug}/`;
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${SITE_URL}/`
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Bus Time Table',
        item: `${SITE_URL}/bus-time-table`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${route.from} to ${route.to} Bus Time Table`,
        item: pageUrl
      }
    ]
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: route.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: route.title,
    description: route.meta_description,
    url: pageUrl,
    datePublished: '2026-10-18',
    dateModified: '2026-10-18',
    inLanguage: ['hi', 'en'],
    mainEntity: {
      '@type': 'Schedule',
      name: `${route.from} to ${route.to} Bus Schedule`,
      scheduleTimezone: 'Asia/Kolkata'
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/60 dark:bg-gray-950 text-gray-800 dark:text-gray-200">
      {/* SEO Tags */}
      <SEO
        title={route.title}
        titleTemplate=""
        description={route.meta_description}
        canonical={`/bus-time-table/${route.slug}/`}
        jsonLd={[breadcrumbJsonLd, faqJsonLd, webPageJsonLd]}
      />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-8">
        {/* Breadcrumb Navigation */}
        <nav aria-label="Breadcrumb" className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          <ol className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <li>
              <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
                Home
              </Link>
            </li>
            <li><ChevronRight className="h-3.5 w-3.5" /></li>
            <li>
              <Link to="/bus-time-table" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
                Bus Time Table
              </Link>
            </li>
            {route.bus_stand === 'Tohana' && (
              <>
                <li><ChevronRight className="h-3.5 w-3.5" /></li>
                <li>
                  <Link to="/bus-time-table/tohana" className="hover:text-blue-600 dark:hover:text-blue-400 transition">
                    Tohana Stand
                  </Link>
                </li>
              </>
            )}
            <li><ChevronRight className="h-3.5 w-3.5" /></li>
            <li className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[200px] sm:max-w-none">
              {route.from} to {route.to}
            </li>
          </ol>
        </nav>

        {/* Top Header Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200/80 dark:border-gray-800 p-6 sm:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 dark:border-gray-800/80 pb-6">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 text-xs font-semibold tracking-wide uppercase border border-blue-100 dark:border-blue-900">
                <Bus className="h-3.5 w-3.5" />
                Haryana Roadways Official Timetable
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight">
                {route.h1}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 max-w-3xl">
                {route.bus_stand} बस स्टैंड से {route.to} के लिए प्रतिदिन {route.total_buses} बस सेवाएं उपलब्ध हैं। पहली बस सुबह {route.first_bus} बजे तथा अंतिम बस {route.last_bus} बजे रवाना होती है।
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-xl transition"
              >
                {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Share2 className="h-4 w-4" />}
                {copied ? 'Link Copied!' : 'Share'}
              </button>
            </div>
          </div>

          {/* Quick Metrics Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6">
            <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-gray-800/50 border border-slate-100 dark:border-gray-800">
              <div className="text-xs text-gray-500 dark:text-gray-400 font-medium flex items-center gap-1.5">
                <Bus className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                Daily Buses
              </div>
              <div className="mt-1 text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {route.total_buses} <span className="text-xs font-normal text-gray-500">Buses/Day</span>
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-gray-800/50 border border-slate-100 dark:border-gray-800">
              <div className="text-xs text-gray-500 dark:text-gray-400 font-medium flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                First Bus (पहली बस)
              </div>
              <div className="mt-1 text-xl sm:text-2xl font-bold text-emerald-700 dark:text-emerald-400 font-mono">
                {route.first_bus}
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-gray-800/50 border border-slate-100 dark:border-gray-800">
              <div className="text-xs text-gray-500 dark:text-gray-400 font-medium flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                Last Bus (अंतिम बस)
              </div>
              <div className="mt-1 text-xl sm:text-2xl font-bold text-purple-700 dark:text-purple-400 font-mono">
                {route.last_bus}
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-gray-800/50 border border-slate-100 dark:border-gray-800">
              <div className="text-xs text-gray-500 dark:text-gray-400 font-medium flex items-center gap-1.5">
                <Building className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                Boarding Stand
              </div>
              <div className="mt-1 text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate">
                {route.bus_stand} Stand
              </div>
            </div>
          </div>
        </div>

        {/* Global Dual Language Disclaimer Banner */}
        <BusDisclaimer />

        {/* Interactive Filters & Departure Schedule Section */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200/80 dark:border-gray-800 shadow-sm overflow-hidden">
          <div className="p-5 sm:p-6 border-b border-gray-100 dark:border-gray-800 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-600" />
                  Departure Timings Table (प्रस्थान समय सारणी)
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Showing {filteredBuses.length} of {allBuses.length} scheduled departures
                </p>
              </div>

              {/* Quick Search */}
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter by time, operator, via..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Via & Time Filter Tabs */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-100 dark:border-gray-800/80">
              {/* Via Points Pills */}
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 mr-1">
                  Via Route:
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedVia('ALL')}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${
                    selectedVia === 'ALL'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
                  }`}
                >
                  All ({allBuses.length})
                </button>
                {route.via_groups && route.via_groups.map((g) => (
                  <button
                    key={g.via}
                    type="button"
                    onClick={() => setSelectedVia(g.via)}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${
                      selectedVia === g.via
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
                    }`}
                  >
                    Via {g.via} ({g.count})
                  </button>
                ))}
              </div>

              {/* Time of Day Pills */}
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 mr-1">
                  Time:
                </span>
                {[
                  { id: 'ALL', label: 'All' },
                  { id: 'MORNING', label: 'Morning (04-12)' },
                  { id: 'AFTERNOON', label: 'Afternoon (12-17)' },
                  { id: 'EVENING', label: 'Evening (17+)' }
                ].map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTimeFilter(t.id)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${
                      timeFilter === t.id
                        ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900 shadow-sm'
                        : 'bg-slate-100 text-gray-600 hover:bg-slate-200 dark:bg-gray-800 dark:text-gray-400'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Semantic Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs sm:text-sm">
              <caption className="sr-only">
                {route.from} to {route.to} bus timetable with departure times, via points, operator and bus service types
              </caption>
              <thead>
                <tr className="bg-slate-50 dark:bg-gray-800/70 border-b border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 font-semibold">
                  <th scope="col" className="py-3.5 px-4 sm:px-6 w-16 text-center">#</th>
                  <th scope="col" className="py-3.5 px-4 sm:px-6">Departure (प्रस्थान)</th>
                  <th scope="col" className="py-3.5 px-4 sm:px-6">From (कहाँ से)</th>
                  <th scope="col" className="py-3.5 px-4 sm:px-6">To (कहाँ तक)</th>
                  <th scope="col" className="py-3.5 px-4 sm:px-6">Via (रूट)</th>
                  <th scope="col" className="py-3.5 px-4 sm:px-6">Service Type</th>
                  <th scope="col" className="py-3.5 px-4 sm:px-6">Operator</th>
                  <th scope="col" className="py-3.5 px-4 sm:px-6 text-center">Days</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {filteredBuses.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-12 text-center text-gray-500 dark:text-gray-400">
                      कोई बस नहीं मिली। फ़िल्टर बदल कर पुनः प्रयास करें।
                    </td>
                  </tr>
                ) : (
                  filteredBuses.map((bus, idx) => {
                    const opBadge = OPERATOR_BADGES[bus.operator] || 'bg-gray-100 text-gray-800';
                    const isHvac = bus.service_type === 'HVAC';

                    return (
                      <tr 
                        key={`${bus.serial_no}-${idx}`} 
                        className="hover:bg-blue-50/40 dark:hover:bg-blue-950/20 transition-colors"
                      >
                        <td className="py-3 px-4 sm:px-6 text-center font-mono text-gray-400">
                          {idx + 1}
                        </td>
                        <td className="py-3 px-4 sm:px-6 font-mono font-bold text-base text-gray-900 dark:text-white">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300">
                            <Clock className="h-3.5 w-3.5" />
                            {bus.departure_time}
                          </span>
                        </td>
                        <td className="py-3 px-4 sm:px-6 font-medium text-gray-900 dark:text-white">
                          {bus.from}
                        </td>
                        <td className="py-3 px-4 sm:px-6 font-semibold text-gray-900 dark:text-white">
                          {bus.to}
                        </td>
                        <td className="py-3 px-4 sm:px-6 text-gray-600 dark:text-gray-300">
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-gray-400 shrink-0" />
                            {bus.via}
                          </span>
                        </td>
                        <td className="py-3 px-4 sm:px-6">
                          <span
                            className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                              isHvac
                                ? 'bg-cyan-100 text-cyan-800 border border-cyan-200 dark:bg-cyan-950/60 dark:text-cyan-300 dark:border-cyan-800'
                                : 'bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300'
                            }`}
                          >
                            {bus.service_type}
                          </span>
                        </td>
                        <td className="py-3 px-4 sm:px-6">
                          <span
                            title={OPERATOR_NAMES[bus.operator] || bus.operator}
                            className={`inline-block px-2.5 py-0.5 rounded-md text-xs font-bold border ${opBadge}`}
                          >
                            {bus.operator}
                          </span>
                        </td>
                        <td className="py-3 px-4 sm:px-6 text-center text-gray-500 dark:text-gray-400 font-medium text-xs">
                          {bus.service_days || 'All Days'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="p-4 bg-slate-50 dark:bg-gray-800/40 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap items-center justify-between gap-2">
            <span>* ऑपरेटर संकेत: HR = हरियाणा रोडवेज, PRTC = पंजाब रोडवेज, PVT = निजी सहकारी समिति</span>
            <span>स्रोत: महाप्रबंधक कार्यालय, हरियाणा रोडवेज फतेहाबाद</span>
          </div>
        </div>

        {/* Unique Data-Driven Content Section (150-250 Words) */}
        <section aria-labelledby="route-guide-heading" className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200/80 dark:border-gray-800 p-6 sm:p-8 shadow-sm space-y-4">
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5 text-blue-600" />
            <h2 id="route-guide-heading" className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              About {route.from} to {route.to} Haryana Roadways Bus Service
            </h2>
          </div>
          
          <div className="prose dark:prose-invert max-w-none text-sm sm:text-base leading-relaxed text-gray-700 dark:text-gray-300 space-y-3">
            <p>{route.intro_text}</p>
          </div>

          {/* Quick Route Highlights Box */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 rounded-xl bg-slate-50 dark:bg-gray-800/50 border border-slate-100 dark:border-gray-800 text-xs sm:text-sm">
            <div>
              <span className="font-semibold text-gray-900 dark:text-white block">प्रमुख ठहराव (Main Via):</span>
              <span className="text-gray-600 dark:text-gray-400">{route.via_points.join(', ')}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-900 dark:text-white block">ऑपरेटर नेटवर्क:</span>
              <span className="text-gray-600 dark:text-gray-400">
                {route.operators.map(op => OPERATOR_NAMES[op] || op).join(', ')}
              </span>
            </div>
            <div>
              <span className="font-semibold text-gray-900 dark:text-white block">पूछताछ सहायता:</span>
              <span className="text-gray-600 dark:text-gray-400">{ENQUIRY_NUMBER}</span>
            </div>
          </div>
        </section>

        {/* Frequently Asked Questions Section */}
        <section aria-labelledby="faqs-heading" className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200/80 dark:border-gray-800 p-6 sm:p-8 shadow-sm space-y-6">
          <div className="flex items-center gap-2 border-b border-gray-100 dark:border-gray-800 pb-4">
            <HelpCircle className="h-6 w-6 text-blue-600" />
            <div>
              <h2 id="faqs-heading" className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Frequently Asked Questions (अक्सर पूछे जाने वाले सवाल)
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Common questions about {route.from} to {route.to} bus timings, stops, and schedule
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {route.faqs.map((faq, index) => {
              const isOpen = openFaqIndex === index;
              return (
                <div
                  key={index}
                  className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden transition"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaqIndex(isOpen ? -1 : index)}
                    className="w-full flex items-center justify-between p-4 text-left font-semibold text-gray-900 dark:text-white hover:bg-slate-50 dark:hover:bg-gray-800/50 transition gap-3"
                  >
                    <span className="text-sm sm:text-base">
                      {faq.question}
                      <span className="block text-xs font-normal text-gray-500 dark:text-gray-400 mt-0.5">
                        {faq.question_hi}
                      </span>
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 text-gray-500 transition-transform duration-200 shrink-0 ${
                        isOpen ? 'rotate-180 text-blue-600' : ''
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-800 bg-slate-50/50 dark:bg-gray-800/30 leading-relaxed">
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Related Routes Cross-linking Section */}
        {relatedRoutes.length > 0 && (
          <section aria-labelledby="related-routes-heading" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 id="related-routes-heading" className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Other Popular Bus Routes from {route.from}
              </h2>
              <Link
                to={route.bus_stand === 'Tohana' ? '/bus-time-table/tohana' : '/bus-time-table'}
                className="text-xs sm:text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
              >
                View all routes <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {relatedRoutes.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/bus-time-table/${rel.slug}`}
                  className="group bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 hover:border-blue-500 dark:hover:border-blue-500 transition shadow-sm hover:shadow"
                >
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                      {rel.total_buses} Buses Daily
                    </span>
                    <span className="font-mono">{rel.first_bus} - {rel.last_bus}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition text-sm sm:text-base">
                    {rel.from} to {rel.to}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                    Via {rel.via_points.join(', ')}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Quick Hub Navigation Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
          <Link
            to="/bus-time-table"
            className="p-4 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:border-blue-500 transition shadow-sm text-center block"
          >
            <Bus className="h-6 w-6 text-blue-600 mx-auto mb-2" />
            <h4 className="font-bold text-sm text-gray-900 dark:text-white">Fatehabad Main Bus Stand</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">All 526 buses & routes</p>
          </Link>
          <Link
            to="/bus-time-table/tohana"
            className="p-4 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:border-blue-500 transition shadow-sm text-center block"
          >
            <Bus className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
            <h4 className="font-bold text-sm text-gray-900 dark:text-white">Tohana Bus Stand</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">All 337 buses & routes</p>
          </Link>
          <Link
            to="/bus-time-table/through-buses"
            className="p-4 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:border-blue-500 transition shadow-sm text-center block"
          >
            <Bus className="h-6 w-6 text-purple-600 mx-auto mb-2" />
            <h4 className="font-bold text-sm text-gray-900 dark:text-white">Through Buses Hub</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">171 buses passing Fatehabad</p>
          </Link>
        </div>
      </div>
    </div>
  );
}
